/**
 * DocumentTile — one card in the document grid.
 *
 * Surfaces only what an FSR / supervisor needs at a glance:
 *   - Big file-type badge (PDF / image / other)
 *   - Filename (truncated; full text in title)
 *   - Plain-language doc-code label (DL_FRONT → "Driver License — Front")
 *   - Captured-by display name (humanized) + relative date
 *
 * Hidden by design (per scope):
 *   - documentId, GUIDs, raw SharePoint URLs
 *   - SharePoint relativePath (used internally for fallback URL only)
 *   - Officer codes — humanized to "Sarah S." style display names
 *
 * Click → fires onPreview(doc).
 */

import type { DocumentRef } from '@retail-ops/api-client';
import { Pill } from '@/components/primitives';
import { DocumentTypeIcon } from './DocumentTypeIcon';
import { humanizeUploader, humanizeDocCode, formatRelativeDay } from './humanize';
import { formatBytes } from '@/hooks/useDocuments';

export interface DocumentTileProps {
  doc: DocumentRef;
  onPreview: (doc: DocumentRef) => void;
  /** When true, the tile shows the cart chip; defaults to off in per-cart views. */
  showCart?: boolean;
  /**
   * AO-DOCUMENTS-SCHEMA-REBUILD (2026-04-25) — when set, a "View related cart"
   * action renders as a chip-button on the tile and deep-links into the
   * originating cart in RAO/Maint. Click is intentionally separated from the
   * preview-open click so the tile acts as a single-purpose affordance.
   */
  onOpenCart?: (doc: DocumentRef) => void;
}

export function DocumentTile({
  doc,
  onPreview,
  showCart = true,
  onOpenCart,
}: DocumentTileProps): JSX.Element {
  const typeLabel = doc.docCode ? humanizeDocCode(doc.docCode) : null;
  // Operational-context chips driven by the v3 ao_Documents column schema.
  const branchLabel = doc.branch ? `Branch ${doc.branch}` : null;
  const isHardHold = !!doc.isHardHold;
  const isTestMode = !!doc.isTestMode;
  return (
    <div className="rdl-tile-wrap">
      <button
        type="button"
        onClick={() => onPreview(doc)}
        className="rdl-tile"
        aria-label={`Open preview for ${doc.fileName}`}
      >
        <div className="rdl-tile__thumb">
          <DocumentTypeIcon contentType={doc.contentType} size={28} />
        </div>
        <div className="rdl-tile__body">
          <div className="rdl-tile__filename" title={doc.fileName}>
            {doc.fileName}
          </div>
          {doc.customerName && (
            <div className="rdl-tile__customer" title={doc.customerName}>
              {doc.customerName}
            </div>
          )}
          {typeLabel && (
            <div className="rdl-tile__doctype">
              <Pill variant="info">{typeLabel}</Pill>
              {branchLabel ? <Pill variant="muted">{branchLabel}</Pill> : null}
              {isHardHold ? <Pill variant="warn">Hard hold</Pill> : null}
              {isTestMode ? <Pill variant="muted">Test mode</Pill> : null}
            </div>
          )}
          <div className="rdl-tile__meta">
            <span>{formatRelativeDay(doc.uploadedAt)}</span>
            <span aria-hidden>·</span>
            <span>{formatBytes(doc.sizeBytes)}</span>
          </div>
          <div className="rdl-tile__officer" title={doc.uploadedBy}>
            By {humanizeUploader(doc.uploadedBy)}
            {showCart && doc.cartId ? <span className="rdl-tile__cartchip"> · in cart</span> : null}
          </div>
        </div>
      </button>
      {onOpenCart && doc.cartId ? (
        <button
          type="button"
          className="rdl-tile__cartlink"
          onClick={(e) => {
            e.stopPropagation();
            onOpenCart(doc);
          }}
          aria-label="View the cart this document came from"
        >
          View related cart
        </button>
      ) : null}
    </div>
  );
}
